<template>
  <div style="overflow-x: auto">
    <h5 class="mb-2 mt-4">
      Участники
    </h5>
    <div class="container">
      <div class="row" v-if="hostTeams.length">
        <div v-for="team in hostTeams"
             :key="'team-' + team.team_id"
             :class="['mr-5', {'team-eliminated': isTeamOut(team)}]">
          <h5 class="mb-4 mt-4">
            Команда: {{ teamName(team) }} Slot: {{ team.slot_number }}
            <b-badge v-if="isTeamOut(team)" variant="dark" class="ml-2">
              Выбыла{{ team.place ? ` — ${team.place} место` : '' }}
            </b-badge>
          </h5>
          <table class="table">
            <thead>
            <tr>
              <th scope="col" class="p-0" style="width: 140px">Nick</th>
              <th scope="col" class="p-0">Kill</th>
              <th scope="col" class="p-0">Up</th>
              <th scope="col" class="p-0">Down</th>
              <th scope="col" class="p-0">Жив</th>
            </tr>
            </thead>
            <tbody>
            <RowInHostsBook v-for="member in team.members"
                            :key="'member-' + team.team_id + '-' + member.user_id"
                            :disabled="isEventEnded"
                            :member="member"
                            :kill-for-send="killForSend(team.team_id, member)"
                            :death-for-send="deathForSend(team.team_id, member)"
                            @update:killForSend="setKillForSend(team.team_id, member, $event)"
                            @update:deathForSend="setDeathForSend(team.team_id, member, $event)"
            />
            </tbody>
          </table>
          <div class="d-flex">
            <b-btn class="mr-2"
                   variant="primary"
                   :disabled="isEventEnded || isSending(team.team_id)"
                   @click.prevent="sendTeamResults(team)"
            >
              Send
            </b-btn>
            <b-btn :disabled="isEventEnded"
                   @click="teamNotEnterToLobby(team)"
            >
              Не вошла
            </b-btn>
          </div>
        </div>

        <b-modal centered
                 id="deathTeamModal"
                 ref="deathTeamModal"
        >
          <p class="my-4">Команда {{ teamName(selectedTeam) }} точно не вошла?</p>
          <template #modal-footer>
            <div>
              <b-btn variant="primary"
                     class="mr-3"
                     @click="confirmTeamNotEnterToLobby()">
                Да
              </b-btn>
              <b-btn variant="danger" @click="$refs.deathTeamModal.hide();">Отмена</b-btn>
            </div>
          </template>
        </b-modal>
      </div>
      <div v-else-if="isEventLoading" class="loading-wrapper">
        <b-spinner class="spinner"></b-spinner>
      </div>
    </div>
  </div>
</template>
<script>

import {BookAdapterMixin} from "@/mixins/Book/BookAdapterMixin";
import RowInHostsBook from "@/components/Live/RowInHostsBook.vue";
import {isTeamEliminated} from "@/common/liveEvent";

export default {
  name: 'HostsLive',
  components: {RowInHostsBook},
  mixins: [BookAdapterMixin],
  props: {
    host: {
      type: Object,
      require: true
    },
  },
  data() {
    return {
      striped: true,
      bordered: true,
      selectedTeam: {},
      /**
       * Неотправленные результаты: {[team_id]: {[user_id]: {kill, death}}}.
       * Живут в компоненте, а не в сторе, поэтому перерисовка таблицы
       * после ответа бэкенда их не сбрасывает.
       */
      teamEdits: {},
      sendingTeams: {},
    }
  },
  computed: {
    hostTeams() {
      return this.teamsOfHost(this.host?.hash);
    },
    isEventEnded() {
      return this.showEventData?.type === 'ended';
    },
    isEventLoading() {
      return !this.showEventData?.teams;
    },
  },
  watch: {
    // при переключении матча незаписанные результаты прошлого матча не нужны
    selectedMatch() {
      this.teamEdits = {};
      this.sendingTeams = {};
    },
  },
  methods: {
    teamName(team) {
      return team?.info?.name ?? '';
    },
    /** Команда выбыла полностью — все игроки мертвы. */
    isTeamOut(team) {
      return isTeamEliminated(team);
    },
    killForSend(teamId, member) {
      return this.teamEdits?.[teamId]?.[member.user_id]?.kill ?? 0;
    },
    deathForSend(teamId, member) {
      return this.teamEdits?.[teamId]?.[member.user_id]?.death ?? (member.death > 0 ? 1 : 0);
    },
    setKillForSend(teamId, member, value) {
      this.setEdit(teamId, member, {kill: value});
    },
    setDeathForSend(teamId, member, value) {
      this.setEdit(teamId, member, {death: value});
    },
    setEdit(teamId, member, patch) {
      if (!this.teamEdits[teamId]) {
        this.$set(this.teamEdits, teamId, {});
      }

      const current = {
        kill: this.killForSend(teamId, member),
        death: this.deathForSend(teamId, member),
      };

      this.$set(this.teamEdits[teamId], member.user_id, {...current, ...patch});
    },
    isSending(teamId) {
      return Boolean(this.sendingTeams[teamId]);
    },
    /** Одна кнопка Send на команду: результаты всех игроков уходят одним запросом. */
    async sendTeamResults(team) {
      if (this.isSending(team.team_id)) return null;

      const actions = team.members.map((member) => ({
        user_id: member.user_id,
        kill: this.killForSend(team.team_id, member),
        death: this.deathForSend(team.team_id, member),
      }));

      this.$set(this.sendingTeams, team.team_id, true);

      try {
        const res = await this.storeKill({
          matchId: this.selectedMatch,
          token: team.hash,
          actions,
        });

        // данные команды пришли в ответе — локальные правки больше не нужны
        this.$set(this.teamEdits, team.team_id, {});

        return res;
      } catch (error) {
        return null;
      } finally {
        this.$set(this.sendingTeams, team.team_id, false);
      }
    },
    teamNotEnterToLobby(team) {
      this.selectedTeam = team;
      this.$refs.deathTeamModal.show();
    },
    async confirmTeamNotEnterToLobby() {
      const teamId = this.selectedTeam?.team_id;

      this.$refs.deathTeamModal.hide();

      await this.storeAllTeamsWithDeath({
        matchId: this.selectedMatch,
        token: this.selectedTeam?.hash,
        teamId,
      });

      if (teamId !== undefined) {
        this.$set(this.teamEdits, teamId, {});
      }
    },
  },
}
</script>

<style scoped lang="scss">
/* блок полностью выбывшей команды визуально гасим */
.team-eliminated {
  opacity: 0.65;

  h5 {
    text-decoration: line-through;
  }
}
</style>
